"use client";
import { useCallback, useEffect, useRef } from "react";
import Image from "next/image";
import SigninContent from "@packages/shared-components/user-profile/signin-signup/signincontent";
import Registration from "@packages/shared-components/user-profile/signin-signup/registration";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const RegistrationModal = ({ onClose }: { onClose: () => void }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();

      // Focus trap: cycle Tab within modal
      if (e.key === "Tab") {
        const modal = modalRef.current;
        if (!modal) return;
        const focusableElements = modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusableElements.length === 0) return;
        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  // Body scroll lock
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Auto-focus first focusable element on mount
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;
    const firstFocusable = modal.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    firstFocusable?.focus();
  }, []);

  return (
    <div
      className="flex items-start md:items-center justify-center backdrop-shadow-white fixed inset-0 z-50 bg-white"
      role="dialog"
      aria-modal="true"
      aria-label="Register"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={modalRef} className="relative shadow-custom-6 w-full md:w-[728px] bg-white md:rounded-[8px] max-h-[100vh] md:max-h-[96vh] overflow-y-auto md:overflow-x-hidden md:scrollbar-hidden">
          <button className="absolute top-[14px] right-[17px] z-[1] cursor-pointer">
          <Image
            className="block"
            src="/static/assets/icons/modal_close.svg"
            width="14"
            height="14"
            alt="modal close"
            onClick={onClose}
          />
        </button>
        <div className="flex flex-col md:flex-row">
        {/* Left panel */}
        <div className="bg-blue-100 px-[16px] py-[40px] md:px-[24px] lg:py-[52px] w-full md:w-[309px] flex-shrink-0 overflow-hidden">
          <SigninContent />
        </div>
        {/* Right panel */}
          <div className="md:m-[40px_20px_0_24px] self-center p-[32px_16px] md:max-h-[537px] md:overflow-y-auto md:overflow-x-hidden md:custom-scrollbar-3 w-full">
              <Registration showLogo={false} isModal={true} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationModal;
